/**
 * CookiePolicyPage.jsx
 * 
 * Cookie policy page for Touchstone. Explains what cookies are used,
 * their purpose, and how users can manage them.
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Footer from './Footer';

export default function CookiePolicyPage() {
  const navigate = useNavigate();

  return (
    <div className="fixed inset-0 overflow-y-auto bg-[#030303] select-text">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#030303]/80 backdrop-blur-md border-b border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link
            to="/home"
            className="text-xl text-white"
            style={{ fontFamily: "'Qulio Display', 'Bodoni Moda', serif", letterSpacing: '0.05em' }}
          >
            TOUCHSTONE
          </Link>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="pt-36 pb-16 px-6">
        <div className="max-w-3xl mx-auto">
          <h1
            className="text-4xl md:text-5xl text-white mb-4"
            style={{ fontFamily: "'Qulio Display', 'Bodoni Moda', serif" }}
          >
            Cookie Policy
          </h1>
          <p className="text-sm text-zinc-500 mb-16">Last updated: February 17, 2026</p>

          <div className="space-y-12 text-zinc-400 leading-relaxed">

            <section>
              <h2 className="text-xl text-white font-medium mb-4">1. What Are Cookies</h2>
              <p>
                Cookies are small text files that are placed on your device when you visit a website. 
                They are widely used to make websites work efficiently, provide a better user experience, 
                and give site owners useful information. Touchstone uses cookies and similar technologies 
                (such as localStorage) sparingly and transparently.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">2. How We Use Cookies</h2>
              <p className="mb-6">
                Touchstone uses minimal cookies and local storage. We categorize them as follows:
              </p>

              {/* Cookie table */}
              <div className="overflow-x-auto rounded-xl border border-white/[0.06]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/[0.06] bg-white/[0.02]">
                      <th className="text-left px-4 py-3 text-zinc-300 font-medium">Type</th>
                      <th className="text-left px-4 py-3 text-zinc-300 font-medium">Purpose</th>
                      <th className="text-left px-4 py-3 text-zinc-300 font-medium">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04]">
                    <tr>
                      <td className="px-4 py-3 text-white">Essential</td>
                      <td className="px-4 py-3">Required for the site to function. Includes session management and security tokens.</td>
                      <td className="px-4 py-3">Session</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-white">Preferences</td>
                      <td className="px-4 py-3">Stores your profile settings, saved workflows, and UI preferences in localStorage.</td>
                      <td className="px-4 py-3">Persistent (until cleared)</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-white">Analytics</td>
                      <td className="px-4 py-3">Privacy-respecting analytics to understand aggregate usage patterns. No advertising profiles are built.</td>
                      <td className="px-4 py-3">Up to 12 months</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">3. What We Don't Use</h2>
              <p className="mb-4">Touchstone does <span className="text-white">not</span> use:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Advertising or retargeting cookies</li>
                <li>Third-party tracking pixels from ad networks</li>
                <li>Cross-site tracking technologies</li>
                <li>Cookies that build advertising or behavioral profiles</li>
                <li>Social media tracking widgets</li>
              </ul>
              <p className="mt-4">
                This is consistent with our commitment to neutrality — Touchstone has no affiliate links, 
                no sponsored content, and no ad-driven tracking of any kind.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">4. Local Storage</h2>
              <p>
                In addition to cookies, Touchstone uses your browser's localStorage to save your preferences, 
                profile data, workflow configurations, and UI settings. This data is stored entirely on your 
                device and is never transmitted to our servers. You can clear this data at any time through 
                your browser's settings.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">5. Managing Cookies</h2>
              <p className="mb-4">
                You can control and manage cookies through your browser settings. Most browsers allow you to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>View what cookies are stored and delete them individually</li>
                <li>Block third-party cookies</li>
                <li>Block cookies from specific sites</li>
                <li>Block all cookies</li>
                <li>Delete all cookies when you close the browser</li>
              </ul>
              <p className="mt-4">
                Please note that blocking essential cookies may affect the functionality of the Touchstone platform. 
                Clearing localStorage will reset your saved preferences and workflow configurations.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">6. Third-Party Services</h2>
              <p>
                If we use any third-party services that set their own cookies (such as privacy-respecting 
                analytics), those services operate under their own cookie and privacy policies. We select 
                only services that align with our commitment to user privacy and do not enable advertising 
                or cross-site tracking.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">7. Changes to This Policy</h2>
              <p>
                We may update this Cookie Policy from time to time to reflect changes in our practices or 
                for operational, legal, or regulatory reasons. When we make material changes, we will update 
                the "Last updated" date at the top of this page.
              </p>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">8. Contact Us</h2>
              <p>
                If you have questions about our use of cookies, please contact us at{' '}
                <a href="mailto:privacy@touchstone.dev" className="text-white hover:underline">
                  privacy@touchstone.dev
                </a>{' '}
                or visit our{' '}
                <Link to="/contact" className="text-white hover:underline">
                  Contact page
                </Link>.
              </p>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
